import React, { useState } from 'react';
import { FIELD_NAME } from '../../../constants/form-constants';
import { formService } from '../../../form-service';
import { SelectExtended } from './core/select-extended';
import { TextInputExtended } from './core/text-input-extended';

interface CountryConfig {
	code: string;
	maxLength?: number;
	regex: RegExp[];
	errorMessage: string;
}

interface CountryOption {
	text: string;
	value: CountryConfig;
}

interface Props {
	label?: string;
	required?: boolean;
	onBlur?: () => void;
}

const countries: CountryOption[] = [
	{
		text: 'España',
		value: {
			code: 'ES',
			maxLength: 8,
			regex: [/^\d{4}[B-DF-HJ-NPR-TV-Z]{3}$/, /^[A-Z]{1,2}\d{4}[A-Z]{1,2}$/],
			errorMessage: 'Ejemplo: 0000BCD o AA0000AA',
		},
	},
	{
		text: 'Andorra',
		value: {
			code: 'AND',
			maxLength: 6,
			regex: [/^[A-Z]\d{4}$/, /^[A-Z]{2}\d{3}$/, /^[A-Z]{2}\d{4}$/],
			errorMessage: 'Ejemplo: W1234, WA123 o WA1234',
		},
	},
	{
		text: 'Alemania',
		value: {
			code: 'DE',
			maxLength: 9,
			regex: [/[A-Z]{2,5}\d{1,4}/],
			errorMessage: 'Ejemplo: AAABB0000',
		},
	},
	{
		text: 'Francia',
		value: {
			code: 'FR',
			maxLength: 9,
			regex: [/[A-HJ-NP-TV-Z]{2}\d{3}[A-HJ-NP-TV-Z]{2}/, /\d{2,4}[A-Z]{2,3}\d{2,3}/, /\d{2,4}[A-Z]{2,3}\d{1}[AB]/],
			errorMessage: 'Ejemplo: AA000AA o 00000AA00',
		},
	},
	{
		text: 'Reino Unido',
		value: {
			code: 'GB',
			maxLength: 7,
			regex: [/[A-Z]{1,2}\d{2}[A-Z]{3}/, /[A-Z]{1}\d{3}[A-Z]{3}/],
			errorMessage: 'Ejemplo: AA00AAA',
		},
	},
	{
		text: 'Italia',
		value: {
			code: 'IT',
			maxLength: 7,
			regex: [/[A-Z]{2}\d{3}[A-Z]{2}/],
			errorMessage: 'Ejemplo: AA000AA',
		},
	},
	{
		text: 'Portugal',
		value: {
			code: 'PT',
			maxLength: 6,
			regex: [/[A-Z]{2}\d{2}[A-Z]{2}/, /\d{2}[A-Z]{2}\d{2}/],
			errorMessage: 'Ejemplo: AA00AA',
		},
	},
	{
		text: 'Otros',
		value: {
			code: 'otros',
			maxLength: undefined,
			regex: [/[a-zA-Z\d\s]{3,}/],
			errorMessage: 'Solo números, letras y espacios',
		},
	},
];

// Crear opciones simples para el SelectExtended
const countryOptions = countries.map((country) => ({
	text: country.text,
	value: country.value.code,
}));

export function PaisMatriculaInput(props: Props) {
	const { label, onBlur } = props;
	const [selectedCountryCode, setSelectedCountryCode] = useState<string>(countries[0].value.code);

	const getSelectedCountry = (): CountryConfig => {
		return countries.find((c) => c.value.code === selectedCountryCode)?.value || countries[0].value;
	};

	const handleCountryChange = (newCountryCode: string) => {
		setSelectedCountryCode(newCountryCode);
		// Limpiar el campo de matrícula cuando cambie el país
		formService.setFieldValue(FIELD_NAME.PLATE, '');
	};

	const selectedCountry = getSelectedCountry();

	return (
		<div style={{ display: 'flex', flexDirection: 'row' }}>
			<div style={{ width: '40%' }}>
				<SelectExtended
					{...props}
					name={FIELD_NAME.PLATE_COUNTRY}
					label={'País'}
					options={countryOptions}
					value={selectedCountryCode}
					onChange={handleCountryChange}
				/>
			</div>
			<div
				style={{
					width: 'calc(60% + 12px)',
					marginLeft: '-12px',
				}}
			>
				<TextInputExtended
					{...props}
					name={FIELD_NAME.PLATE}
					label={label || 'Matrícula'}
					validate={(value) => selectedCountry.regex.some((regex: RegExp) => regex.test(value))}
					errorMessage={selectedCountry.errorMessage}
					maxLength={selectedCountry.maxLength}
					onChange={(e: any) => {
						if (selectedCountry.code != 'otros') {
							e.target.value = e.target.value.toUpperCase().replace('-', '');
						}
					}}
					onBlur={onBlur}
				/>
			</div>
		</div>
	);
}
